import { useState, type JSX } from "react";
import type {
  CaseDto,
  ConversationEntry,
  EvaluationEvent,
} from "../api/types";
import { CriterionProgressMeter } from "../components/CriterionProgressMeter";

interface PlayPageProps {
  gameCase: CaseDto;
  entries: readonly ConversationEntry[];
  evaluation: EvaluationEvent | null;
  hintLevel: 0 | 1 | 2 | 3;
  isBusy: boolean;
  error: string | null;
  onSend: (content: string) => Promise<void>;
  onHint: () => Promise<void>;
  onGiveUp: () => Promise<void>;
  onLogout: () => void;
}

function getEntryLabel(entry: ConversationEntry): string {
  switch (entry.kind) {
    case "gatekeeper":
      return "Motpart";
    case "player":
      return "Du";
    case "evaluation":
      return "Veileder";
    case "hint":
      return `Hint ${entry.level}`;
    default: {
      const exhaustiveCheck: never = entry;
      return exhaustiveCheck;
    }
  }
}

/** Presents the case as a threaded, criterion-safe conversation. */
export function PlayPage({
  gameCase,
  entries,
  evaluation,
  hintLevel,
  isBusy,
  error,
  onSend,
  onHint,
  onGiveUp,
  onLogout,
}: PlayPageProps): JSX.Element {
  const [response, setResponse] = useState("");

  const sendResponse = (): void => {
    const content = response.trim();
    if (content.length === 0)
      return;
    setResponse("");
    void onSend(content);
  };

  return (
    <main className="page play-page">
      <header className="toolbar">
        <div>
          <p className="eyebrow">{gameCase.topicName} · {gameCase.difficulty}</p>
          <h1>{gameCase.title}</h1>
        </div>
        <button type="button" className="secondary" onClick={onLogout}>Logg ut</button>
      </header>

      <section className="case-brief" aria-labelledby="scenario-heading">
        <h2 id="scenario-heading">Situasjonen</h2>
        <p>{gameCase.description}</p>
        <h3>Læringsmål</h3>
        <p>{gameCase.learningObjective}</p>
      </section>

      {evaluation !== null && <CriterionProgressMeter progress={evaluation.criteriaProgress} />}

      <section aria-labelledby="conversation-heading">
        <h2 id="conversation-heading">Samtale</h2>
        <ol className="conversation" aria-live="polite">
          {entries.map((entry) => (
            <li className={`message ${entry.kind}`} key={entry.id}>
              <strong>{getEntryLabel(entry)}</strong>
              <p>{entry.text}</p>
            </li>
          ))}
        </ol>
      </section>

      {error !== null && <p role="alert">{error}</p>}

      <label htmlFor="response">Ditt svar</label>
      <textarea
        id="response"
        value={response}
        onChange={(event) => { setResponse(event.target.value); }}
        placeholder="Skriv hvordan du vil håndtere situasjonen."
        disabled={isBusy}
        rows={5}
      />
      <div className="choices">
        <button
          type="button"
          disabled={isBusy || response.trim().length === 0}
          onClick={sendResponse}
        >
          {isBusy ? "Sender …" : "Send svar"}
        </button>
        {gameCase.hasHints && hintLevel < 3 && (
          <button type="button" className="secondary" disabled={isBusy} onClick={() => { void onHint(); }}>
            Be om hint {hintLevel + 1} av 3
          </button>
        )}
      </div>

      <section className={evaluation?.canComplete === true ? "unlock unlocked" : "unlock"}>
        <p>
          {evaluation?.canComplete === true
            ? "Du har vist nok forståelse. Oppsummeringen låses opp nå."
            : "Fullføring låses opp når du har dekket de nødvendige vurderingene."}
        </p>
        <button
          type="button"
          className="secondary"
          disabled={isBusy}
          onClick={() => { void onGiveUp(); }}
        >
          Gi opp og se oppsummeringen
        </button>
      </section>
    </main>
  );
}
